import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import CheckWhiteIcon from "@styles/all-images/icons/checkwhite.svg";
import CloseBlueIcon from "@styles/all-images/icons/closeblue.svg";
import { toast } from "react-toastify";
import { IDocument } from "react-doc-viewer";
import DocViewerComponent from "@/components/common/DocViewer";
import LiveChatIcon from "@styles/all-images/icons/ChatIconBlue.svg";

import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import DocumentDetails, {
  DocumentDetail,
  DocumentType,
} from "./components/DocumentDetails";
import RejectionReason from "./components/RejectionReason";
import TierConfigurationRejectionReason from "@/pages/TierConfiguration/TierConfigurationRejectionReason";
import { EndpointType } from "@/constants/api";
import CustomLoader from "@/components/common/loader/SpinnerLoader";
import { IMTSDocumentApprovalStatus } from "@/constants/enums/im-ts-document-status.enum";
import SignDocumentViewer from "@/pages/SignedDocuments/components/SignDocumentViewer";
import { usePermissionState } from "@/providers/PermissionProvider";
import {
  DocumentsPermissions,
  SignPermissions,
} from "@/constants/permission-modules/base-permissions";
import { CreateGroupResponse } from "@/pages/LiveChat/types/api-response-types";
import StandaloneChatDrawer, {
  StandaloneChatDrawerType,
} from "@/pages/LiveChat/StandaloneChatDrawer";

const DocumentView = ({
  isSignedDocument = false,
}: {
  isSignedDocument?: boolean;
}) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const documentType = searchParams.get("type") as DocumentType;
  const documentHistory = searchParams.get("history") === "true";
  const navigate = useNavigate();
  const dispatch = useDispatch<AppThunkDispatch>();

  const [documentDetail, setDocumentDetail] = useState<DocumentDetail | null>(
    null,
  );
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState<IDocument[]>([]);
  const [signingUrl, setSigningUrl] = useState<string | null>(null);
  const [showCreateTSConfirm, setShowCreateTSConfirm] = useState(false);
  const [individualChatUserId, setIndividualChatUserId] = useState<
    number | null
  >(null);
  const [individualChatRoomId, setIndividualChatRoomId] = useState<
    string | null
  >(null);
  const [isIndividualChatOpen, setIsIndividualChatOpen] = useState(false);
  const [isIndividualChatCreationLoading, setIsIndividualChatCreationLoading] =
    useState<{ [key: number]: boolean }>({});

  const { isPermitted } = usePermissionState();

  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const userType = JSON.parse(localStorage.getItem("user") || "{}").user_type
    ?.id;

  const fetchDocumentDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      let endpoint: EndpointType;
      if (documentType === DocumentType.IM) {
        endpoint = documentHistory ? "viewIMDocumentHistory" : "viewIMDocument";
      } else {
        endpoint = documentHistory ? "viewTSDocumentHistory" : "viewTSDocument";
      }
      const { authenticatedCall } = actionsHandler(endpoint);
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [`${id}/`],
        }),
      );
      setDocumentDetail(response.data.data);
      handleSettingDocuments(response.data.data);
    } catch (error) {
      console.error("Error fetching document:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id, documentType, dispatch]);

  useEffect(() => {
    if (id && documentType) {
      fetchDocumentDetail();
    }
  }, [id, documentType, dispatch, fetchDocumentDetail]);

  const handleSettingDocuments = (docDetail: DocumentDetail) => {
    if (!docDetail?.document_url) {
      setDocuments([{ uri: "No Document Available", fileType: "" }]);
      return;
    }
    setDocuments([
      {
        uri: docDetail.document_url,
        fileType: docDetail.document_url.split(".").pop() || "",
      },
    ]);
  };
  useEffect(() => {
    if (signingUrl) {
      console.log("signingUrl", signingUrl);
    }
  }, [signingUrl]);
  const handleApproveReject = async (isRejected: boolean, reason?: string) => {
    try {
      setIsLoading(true);
      const endpoint: EndpointType = "approveRejectIMTSDocument";
      const { authenticatedCall } = actionsHandler(endpoint);
      if (!isRejected) {
        setIsApproveLoading(true);
      } else {
        setIsRejectionModalOpen(false);
      }
      const response = await dispatch(
        authenticatedCall({
          method: "put",
          payload: {
            status: isRejected
              ? IMTSDocumentApprovalStatus.Rejected
              : IMTSDocumentApprovalStatus.Approved,
            note: reason,
          },
          customParams: [`${id}/`],
        }),
      );

      if (response.ok) {
        if (IMTSDocumentApprovalStatus.Approved) {
          setSigningUrl(response.data.data?.signing_url);
        }
        toast.success(response.data.message);
        setIsRejectionModalOpen(false);
        fetchDocumentDetail();
      }
    } catch (error) {
      console.error("error: ", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleCreateTsDocument = useCallback((doc: any) => {
    setShowCreateTSConfirm(true);
  }, []);
  const confirmCreateTS = () => {
    navigate(`/document/im-document/create-ts-document/${documentDetail?.id}/`);
    setShowCreateTSConfirm(false);
  };
  const getActionAllowed = useCallback(() => {
    let isAllowed = false;
    let permissionAllowed = false;
    const isPending =
      documentDetail?.approval_status === IMTSDocumentApprovalStatus.Pending;
    const isApproved =
      documentDetail?.approval_status === IMTSDocumentApprovalStatus.Approved;
    const isRejected =
      documentDetail?.approval_status === IMTSDocumentApprovalStatus.Rejected;

    if (documentType === DocumentType.IM) {
      permissionAllowed = isPermitted(
        isSignedDocument
          ? SignPermissions.APPROVE_REJECT_SIGN
          : DocumentsPermissions.APPROVE_REJECT_IM_DOCUMENTS,
      );
      isAllowed =
        userType === UserTierType.Tier1 || userType === UserTierType.Tier2;
    } else if (documentType === DocumentType.TS) {
      permissionAllowed = isPermitted(
        isSignedDocument
          ? SignPermissions.APPROVE_REJECT_SIGN
          : DocumentsPermissions.APPROVE_REJECT_TERMS_SHEET_DOCUMENTS,
      );
      if (
        userType === UserTierType.Tier3 &&
        documentDetail?.is_received === true
      ) {
        isAllowed = true;
      } else if (userType === UserTierType.Tier4) {
        isAllowed = true;
      }
    }

    if (documentHistory) {
      isAllowed = false;
    }

    return { isAllowed, isPending, isApproved, isRejected, permissionAllowed };
  }, [documentDetail, documentType, userType]);

  const handleBack = () => {
    let fromPathUrl = "";
    if (isSignedDocument) {
      if (
        documentDetail?.approval_status == IMTSDocumentApprovalStatus.Approved
      ) {
        fromPathUrl = "/signed-documents?tab=signedDocuments";
      } else {
        fromPathUrl = "/signed-documents?tab=toBeSignedDocuments";
      }
    } else {
      if (documentType === DocumentType.IM) {
        fromPathUrl = "/document/im-document";
      } else {
        fromPathUrl = "/document/terms-sheet";
      }
    }
    navigate(fromPathUrl);
  };

  const handleCreatingIndividualChatRoom = (
    userId: number,
    id: number,
  ): Promise<CreateGroupResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        setIsIndividualChatCreationLoading((prev) => ({
          ...prev,
          [id]: true,
        }));
        const { authenticatedCall } = actionsHandler("createIndividualChat");
        const response = await dispatch(
          authenticatedCall({
            method: "post",
            payload: {
              recipient_id: userId,
            },
          }),
        );

        if (response.ok) {
          resolve(response.data as CreateGroupResponse);
        }
      } catch (error) {
        console.error("Error creating individual chat:", error);
        reject(error);
      } finally {
        setIsIndividualChatCreationLoading((prev) => ({
          ...prev,
          [id]: false,
        }));
      }
    });
  };

  const handleIndividualChat = async (item: DocumentDetail | null) => {
    if (!item || !item.sent_by_id) {
      return;
    }
    if (item.room_id) {
      setIndividualChatRoomId(item.room_id?.toString() || null);
    } else {
      const data = await handleCreatingIndividualChatRoom(
        item.sent_by_id,
        item.id,
      );
      setIndividualChatRoomId(data.room_id.toString() || null);
    }
    setIndividualChatUserId(item.sent_by_id);
    setIsIndividualChatOpen(true);
  };

  useEffect(() => {
    if (!isIndividualChatOpen) {
      setIndividualChatRoomId(null);
      setIndividualChatUserId(null);
    }
  }, [isIndividualChatOpen]);

  if (isLoading) {
    return (
      <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-h-screen">
        {isApproveLoading && (
          <div className="tw-text-center tw-text-base tw-font-semibold tw-text-[#232323]">
            Please wait, the signature screen is loading...
          </div>
        )}
        <CustomLoader loading={true} isWrapper={true} size="50" />
      </div>
    );
  }

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a onClick={handleBack}>
                <img src={BackIcon} alt="Back Icon" />
              </a>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                {documentDetail?.document_name ||
                  documentDetail?.document_title}
              </h1>
            </div>

            <div className="tw-flex tw-items-center tw-gap-4">
              {isSignedDocument && (
                <button onClick={() => handleIndividualChat(documentDetail)}>
                  {isIndividualChatCreationLoading[documentDetail?.id || 0] ? (
                    <CustomLoader loading={true} isWrapper={true} size="20" />
                  ) : (
                    <img src={LiveChatIcon} alt="LiveChatIcon Icon" />
                  )}
                </button>
              )}
              {getActionAllowed().isAllowed && (
                <>
                  {getActionAllowed().isPending &&
                    !signingUrl &&
                    getActionAllowed().permissionAllowed && (
                      <>
                        <button
                          onClick={() => setIsRejectionModalOpen(true)}
                          className="tw-px-4 tw-py-2 tw-border tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-border-[#146C94] tw-rounded-lg tw-text-[#146C94]"
                        >
                          Reject
                          <img
                            src={CloseBlueIcon}
                            alt="Close Icon"
                            className="tw-w-6 tw-h-6 tw-max-w-6 tw-max-h-6 tw-min-w-6 tw-min-h-6"
                          />
                        </button>
                        <button
                          onClick={() => handleApproveReject(false)}
                          className="tw-bg-[#146C94] tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-text-white tw-rounded-lg tw-px-4 tw-py-2"
                        >
                          Approve & Sign
                          <img
                            src={CheckWhiteIcon}
                            alt="Check Icon"
                            className="tw-w-6 tw-h-6 tw-max-w-6 tw-max-h-6 tw-min-w-6 tw-min-h-6"
                          />
                        </button>
                      </>
                    )}

                  {getActionAllowed().isApproved &&
                    !documentDetail?.is_term_sheet_created &&
                    !isSignedDocument &&
                    (userType === UserTierType.Tier1 ||
                      userType === UserTierType.Tier2) &&
                    isPermitted(
                      DocumentsPermissions.CREATE_TERMS_SHEET_DOCUMENTS,
                    ) && (
                      <button
                        onClick={() => handleCreateTsDocument(documentDetail)}
                        className="tw-bg-[#146C94] tw-flex tw-items-center tw-gap-2 tw-font-semibold tw-text-white tw-rounded-lg tw-px-4 tw-py-2"
                      >
                        Create Terms Sheet
                      </button>
                    )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`${!signingUrl ? "tw-p-6 tw-mt-[167px] tw-gap-6 tw-flex-col tw-flex" : "tw-mt-[167px]"}`}
      >
        {!signingUrl && (
          <div className="tw-flex tw-items-start tw-gap-6">
            <div className="tw-w-[20%]">
              <DocumentDetails
                document={documentDetail}
                type={documentType}
                isSignedDocument={isSignedDocument}
              />
            </div>

            <div className="tw-w-[60%]">
              <div className="">
                <DocViewerComponent documents={documents} />
              </div>
            </div>

            <div className="tw-w-[20%]">
              {getActionAllowed().isRejected && (
                <RejectionReason
                  rejectedBy={documentDetail?.rejected_by}
                  reason={documentDetail?.note}
                  rejectedAt={documentDetail?.rejected_at}
                />
              )}
            </div>
          </div>
        )}
        {signingUrl && (
          <div className="tw-w-full tw-h-[75vh]">
            <SignDocumentViewer signingUrl={signingUrl} onClose={() => {}} />
          </div>
        )}
      </div>

      {isRejectionModalOpen && (
        <TierConfigurationRejectionReason
          onClose={() => setIsRejectionModalOpen(false)}
          onSubmit={(reason) => handleApproveReject(true, reason)}
          isLoading={isLoading}
        />
      )}

      {showCreateTSConfirm && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-w-[400px]">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
              <div></div>
              <h2 className="tw-text-base tw-font-semibold">
                Confirm Create Terms Sheet
              </h2>
              <button onClick={() => setShowCreateTSConfirm(false)}>
                <svg
                  className="tw-w-5 tw-h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="tw-p-4 tw-text-center tw-flex tw-justify-center tw-gap-2 tw-py-5 tw-px-6">
              <h6 className="tw-text-[#232323] tw-text-base tw-font-semibold tw-gap-1">
                Are you sure you want to create a Terms Sheet for this client?
              </h6>
            </div>

            <div className="tw-flex tw-space-x-4 tw-py-5 tw-px-12">
              <button
                onClick={() => setShowCreateTSConfirm(false)}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              >
                Cancel
              </button>
              <button
                onClick={confirmCreateTS}
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {individualChatUserId && individualChatRoomId && (
        <StandaloneChatDrawer
          isChatNotificationOpen={isIndividualChatOpen}
          setIsChatNotificationOpen={setIsIndividualChatOpen}
          userId={individualChatUserId}
          customChatRoomId={individualChatRoomId}
          type={StandaloneChatDrawerType.USER}
        />
      )}
    </div>
  );
};

export default DocumentView;
